import { Flex, Link, OnboardingLayout, Text } from "@wayflyer/flyui";
import { useSuspenseQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { handleContactTeam } from "shared-utils";
import { useCustomerAppConfig } from "undercarriage";

import { IncompleteStepsModal } from "../../../components/incomplete-steps-modal";
import { FINANCIAL_DOCUMENTS_STEP_ID, FINANCIAL_DOCUMENTS_TYPES } from "../../../hooks/navigation-next";
import { getFundingApplicationStagesQueryOptions } from "../../../queries/funding-application-configuration/queries";
import { UploadDocumentType } from "../../../types";
import { PoolBancarioDocumentUpload, CirbeDocumentUpload, useDocumentNavigationActions, MESSAGES } from "../components";

export const FinancialDocumentsUploadPage = () => {
  const intl = useIntl();
  const {
    data: { customerDomain },
  } = useCustomerAppConfig();
  const { data: fundingApplicationStages } = useSuspenseQuery(getFundingApplicationStagesQueryOptions(customerDomain));

  // Only render/require documents this application's configuration actually has a stage for.
  const configuredDocumentTypes = FINANCIAL_DOCUMENTS_TYPES.filter((documentType) =>
    fundingApplicationStages.documents?.some((stage) => stage.documentType === documentType)
  );
  const singleConfiguredDocumentType = configuredDocumentTypes.length === 1 ? configuredDocumentTypes[0] : undefined;
  const singleDocumentName =
    singleConfiguredDocumentType &&
    intl.formatMessage(
      singleConfiguredDocumentType === UploadDocumentType.CIRBE ? MESSAGES.cirbeTitle : MESSAGES.poolBancarioTitle
    );

  const { primaryAction, secondaryAction, applicationSubmit, documentErrors } = useDocumentNavigationActions({
    forType: FINANCIAL_DOCUMENTS_STEP_ID,
    documentTypes: configuredDocumentTypes,
  });

  return (
    <>
      <IncompleteStepsModal {...applicationSubmit} />
      <OnboardingLayout
        variant="wide"
        title={intl.formatMessage(MESSAGES.financialDocumentsTitle)}
        subtitle={
          singleDocumentName
            ? intl.formatMessage(MESSAGES.financialDocumentsSubtitleSingle, { documentName: singleDocumentName })
            : intl.formatMessage(MESSAGES.financialDocumentsSubtitle)
        }
        actions={{ primary: primaryAction, secondary: secondaryAction }}
      >
        <Flex direction="column" gap="6" maxWidth="550px">
          {configuredDocumentTypes.includes(UploadDocumentType.POOL_BANCARIO) && (
            <PoolBancarioDocumentUpload
              error={documentErrors[UploadDocumentType.POOL_BANCARIO]}
              showConstraints={!!singleDocumentName}
              variant={singleDocumentName ? "dropzone" : "compact"}
            />
          )}
          {configuredDocumentTypes.includes(UploadDocumentType.CIRBE) && (
            <CirbeDocumentUpload
              error={documentErrors[UploadDocumentType.CIRBE]}
              showConstraints={!!singleDocumentName}
              variant={singleDocumentName ? "dropzone" : "compact"}
            />
          )}
          <Flex direction="column" gap="1">
            <Text size="sm" fontWeight="medium">
              {intl.formatMessage(MESSAGES.financialDocumentsNeedHelpHeading)}
            </Text>
            <Text size="sm" color="label">
              <FormattedMessage
                {...(singleDocumentName
                  ? MESSAGES.financialDocumentsNeedHelpDescriptionSingle
                  : MESSAGES.financialDocumentsNeedHelpDescription)}
                values={{
                  a1: (chunks: ReactNode) => (
                    <Link
                      url="https://help-center.wayflyer.com/en/articles/284420-process-for-accounting-documents-uploads-susa-bankenspeigel-and-more"
                      external
                      suppressExternalIcon
                    >
                      {chunks}
                    </Link>
                  ),
                  a2: (chunks: ReactNode) => <Link onClick={handleContactTeam}>{chunks}</Link>,
                }}
              />
            </Text>
          </Flex>
        </Flex>
      </OnboardingLayout>
    </>
  );
};
